import React, { useEffect, useState } from "react";
import lodash from "lodash";
import { useForm } from "react-hook-form";
import { Guid } from "guid-typescript";
import { toast } from "react-toastify";

// Services
import { getTranslateByKey, isNullUndefined, removeEmptyValues } from "@/main/services/utils";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";

// Models
import { KeyValue } from "@/main/models/common";
import { PrioritySuppliersResponse } from "@/main/models/response/PrioritySuppliersResponse";
import { SupplierFilterRequest } from "@/main/models/request";
import { PrioritySuppliersRequest } from "@/main/models/request/prioritySuppliersRequest";

// Reducer
import { getPartType, usePartTypeReducer } from "@/main/store/partType";
import { useDispatch } from "react-redux";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getClaimType } from "@/main/store/claimType";
import { getSuppliers, useSupplierReducer } from "@/main/store/supplier";

// Api
import { prioritySupplierApi } from "@/main/api";

// Components
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import useAsyncModalConfirmReason from "./../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

// Styles and local components
import SecondaryButton from "../button/secondaryButton";
import KogInputTextControlledNew from "../inputText/inputTextControlledNew";

import * as S from "./styles";

interface IPurchaseCriteriaModal {
	guidInsurer?: string | null;
	guidBodyShop?: string | null;
	dataPrioritySuppliersGrid: PrioritySuppliersResponse[];
	setPrioritySuppliersGrid: React.Dispatch<React.SetStateAction<PrioritySuppliersResponse[]>>;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
function PrioritySuppliersModal({
	guidInsurer = null,
	guidBodyShop = null,
	dataPrioritySuppliersGrid,
	setPrioritySuppliersGrid,
	setOpen,
}: IPurchaseCriteriaModal) {
	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	const dispatch = useDispatch();

	const partTypeReducer = usePartTypeReducer();
	const supplierReducer = useSupplierReducer();
	const countryReducer = useCountryReducer();
	const isDisabledSupplier = () => supplierReducer.suppliers.data === null;

	const INITIAL_FORM_STATE_SAVE = {
		zipCodeStart: "" as string,
		zipCodeEnd: "" as string,
		partType: null as KeyValue | null,
		supplier: null as KeyValue | null,
		advantageRate: "" as string,
	};

	type IPrioritySuppliersFormSave = typeof INITIAL_FORM_STATE_SAVE;
	const {
		control,
		formState: { errors },
		clearErrors,
		setValue,
		reset,
		setError,
		handleSubmit,
	} = useForm<IPrioritySuppliersFormSave>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE_SAVE,
	});

	async function onSubmitSave(data: IPrioritySuppliersFormSave) {
		const newInitialZipCode = Number(data.zipCodeStart);
		const newFinalZipCode = Number(data.zipCodeEnd);
		const haveLowerInitialZipCode = newInitialZipCode <= newFinalZipCode;
		if (!haveLowerInitialZipCode) {
			setError("zipCodeStart", {
				type: "initialLessEndZipcode",
				message: getTranslateByKey("information.initialLessEndZipcode", true),
			});
			setError("zipCodeEnd", {
				type: "initialLessEndZipcode",
				message: getTranslateByKey("information.initialLessEndZipcode", true),
			});
			return;
		}

		const isRange = dataPrioritySuppliersGrid.map((item) => {
			const isSameSupplier = item.supplier === data.supplier?.value;
			const isSamePartType =
				item.guidPartType === Guid.EMPTY || item.partType === data.partType?.value || data.partType === null;

			const isRangedInitialZipCode =
				isSameSupplier &&
				isSamePartType &&
				(lodash.inRange(newInitialZipCode, Number(item.zipCodeStart), Number(item.zipCodeEnd)) ||
					newInitialZipCode === Number(item.zipCodeStart) ||
					newInitialZipCode === Number(item.zipCodeEnd));

			const isRangedItemInitialZipCode =
				isSameSupplier &&
				isSamePartType &&
				lodash.inRange(Number(item.zipCodeStart), newInitialZipCode, newFinalZipCode);

			const isRangedFinalZipCode =
				isSameSupplier &&
				isSamePartType &&
				(lodash.inRange(newFinalZipCode, Number(item.zipCodeStart), Number(item.zipCodeEnd)) ||
					newFinalZipCode === Number(item.zipCodeStart) ||
					newFinalZipCode === Number(item.zipCodeEnd));

			const isRangedItemFinalZipCode =
				isSameSupplier && isSamePartType && lodash.inRange(Number(item.zipCodeEnd), newInitialZipCode, newFinalZipCode);

			const isRangedItemSupplier =
				isSameSupplier &&
				isSamePartType &&
				(isRangedItemFinalZipCode || isRangedFinalZipCode || isRangedItemInitialZipCode || isRangedInitialZipCode);

			if (isRangedItemSupplier) {
				setError("partType", {
					type: "zipCodeAlreadyRegisteredForThisSupplierAndPartType",
					message: getTranslateByKey("information.zipCodeAlreadyRegisteredForThisSupplierAndPartType", true),
				});
			}

			if (isRangedInitialZipCode && (isSamePartType || isRangedItemSupplier)) {
				setError("zipCodeStart", {
					type: "initialCEPRegisteredForThisSupplier",
					message: getTranslateByKey("information.initialCEPRegisteredForThisSupplier", true),
				});
				setError("supplier", {
					type: "zipCodeRegisteredForThisSupplier",
					message: getTranslateByKey("information.zipCodeRegisteredForThisSupplier", true),
				});
			}
			if (isRangedItemInitialZipCode && (isSamePartType || isRangedItemSupplier)) {
				setError("zipCodeEnd", {
					type: "existsRangeForThisSupplier",
					message: getTranslateByKey("information.existsRangeForThisSupplier", true),
				});
				setError("supplier", {
					type: "zipCodeRegisteredForThisSupplier",
					message: getTranslateByKey("information.zipCodeRegisteredForThisSupplier", true),
				});
			}

			if (isRangedFinalZipCode && (isSamePartType || isRangedItemSupplier)) {
				setError("zipCodeEnd", {
					type: "finalZipCodeRegisteredForThisSupplier",
					message: getTranslateByKey("information.finalZipCodeRegisteredForThisSupplier", true),
				});
				setError("supplier", {
					type: "zipCodeRegisteredForThisSupplier",
					message: getTranslateByKey("information.zipCodeRegisteredForThisSupplier", true),
				});
			}

			if (isRangedItemFinalZipCode && (isSamePartType || isRangedItemSupplier)) {
				setError("zipCodeStart", {
					type: "existsRangeForThisSupplier",
					message: getTranslateByKey("information.existsRangeForThisSupplier", true),
				});
				setError("supplier", {
					type: "zipCodeRegisteredForThisSupplier",
					message: getTranslateByKey("information.zipCodeRegisteredForThisSupplier", true),
				});
			}

			return isRangedInitialZipCode || isRangedFinalZipCode || isRangedItemInitialZipCode || isRangedItemFinalZipCode;
		});

		const haveErrorRage = isRange.some((item) => item);
		if (haveErrorRage) {
			return;
		}

		let resultConfirm = null;
		let filesRequest = undefined;

		if (guidInsurer || guidBodyShop) {
			resultConfirm = await getConfirmationReason(
				getTranslateByKey("information.confirmChangeTitle"),
				ReasonTypeEnum.ChangeCompany,
			);

			if (!resultConfirm.confirmed) {
				setLoadingSave(false);
				return;
			}

			if (resultConfirm.files && resultConfirm.files.length > 0) {
				filesRequest = await Promise.all(
					resultConfirm.files.map(async (item) => {
						const base64DataUrl = await toBase64(item);
						return {
							fileName: item.name,
							contentType: item.type,
							fileBase64: base64DataUrl as string,
						};
					}),
				);
			}
		}

		setLoadingSave(true);

		const prioritySupplierRequest = removeEmptyValues({
			zipCodeStart: data.zipCodeStart,
			zipCodeEnd: data.zipCodeEnd,
			guidPartType: data.partType?.key.toString(),
			guidSupplier: data.supplier?.key.toString(),
			guidPrioritySupplier: "",
			guidBodyshop: guidBodyShop,
			guidInsurer: guidInsurer,
			advantageRate: Number(parseFloat(data.advantageRate.replace(",", "."))),
		}) as PrioritySuppliersRequest;

		if (guidInsurer || guidBodyShop) {
			prioritySupplierRequest.changeJustification = {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			};
		}

		prioritySupplierApi
			.savePrioritySupplier(prioritySupplierRequest)
			.then((res) => {
				const resData = res.data.result;
				if (!isNullUndefined(resData)) {
					setPrioritySuppliersGrid((prevState) => {
						return [...prevState, resData];
					});
				}
				toast.success(getTranslateByKey("information.prioritySuppliersSavedSuccessfully"));
				reset(INITIAL_FORM_STATE_SAVE);
				setOpen(false);
			})
			.finally(() => setLoadingSave(false));
	}

	useEffect(() => {
		const errorsZipCode = Object.keys(errors).filter((item) => item.includes("ZipCode"));
		if (!errorsZipCode) {
			clearErrors("zipCodeStart");
			clearErrors("zipCodeEnd");
			clearErrors("supplier");
		}
	}, [JSON.stringify(errors)]);

	const optionDefaultCountryDrop =
		countryReducer.countriesUser.data !== null && countryReducer.countriesUser.data.length === 1
			? countryReducer.countriesUser.data[0]
			: null;

	useEffect(() => {
		dispatch(getPartType());
		dispatch(getCountriesUser());
		dispatch(getClaimType());
	}, []);

	useEffect(() => {
		if (optionDefaultCountryDrop?.key) {
			const request: SupplierFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getSuppliers(request));
		}
	}, [optionDefaultCountryDrop]);

	const MAX_LENGTH_ZIP_CODE = 8;
	const MAX_LENGTH_ADVANTAGE_FACTOR = 5;

	return (
		<>
			<S.Title>{getTranslateByKey("information.registerPrioritySupplier")}</S.Title>
			<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
				<S.Container>
					<KogInputTextControlledNew
						hasOnlyNumberInteger
						required
						id="id-prioritySuppliersModal-txt-zipCodeStart"
						name="zipCodeStart"
						label={getTranslateByKey("information.initialZipCode")}
						control={control}
						inputProps={{ maxLength: MAX_LENGTH_ZIP_CODE }}
						onChangeCustom={() => {
							if (
								errors.supplier?.type === "initialCEPRegisteredForThisSupplier" ||
								errors.supplier?.type === "existsRangeForThisSupplier" ||
								errors.supplier?.type === "finalZipCodeRegisteredForThisSupplier"
							) {
								clearErrors("supplier");
							}
						}}
					/>
					<KogInputTextControlledNew
						hasOnlyNumberInteger
						required
						id="id-prioritySuppliersModal-txt-zipCodeEnd"
						name="zipCodeEnd"
						label={getTranslateByKey("information.finalZipCode")}
						control={control}
						inputProps={{ maxLength: MAX_LENGTH_ZIP_CODE }}
					/>
					<KogAutoCompleteControlled
						id="id-prioritySuppliersModal-multiSelect-partTypes"
						nameCustom="partType"
						label={getTranslateByKey("information.partTypes")}
						control={control}
						isDefaultValueUnique
						options={partTypeReducer.partType.data || []}
						isLoading={partTypeReducer.partType.isLoading}
						setValueCustom={setValue}
						onChangeCustom={() => {}}
					/>

					<KogAutoCompleteControlled
						id="id-prioritySuppliersModal-select-supplier"
						nameCustom="supplier"
						label={getTranslateByKey("information.supplier")}
						isDefaultValueUnique
						control={control}
						rulesCustom={{ required: true }}
						setValueCustom={setValue}
						options={supplierReducer.suppliers.data}
						isLoading={supplierReducer.suppliers.isLoading}
						disabled={isDisabledSupplier()}
						onChangeCustom={() => {}}
					/>
					<KogInputTextControlledNew
						hasOnlyNumber
						rangeOnlyNumber={{
							min: 0,
							max: 100,
						}}
						numberOfDecimals={2}
						required
						id="id-prioritySuppliersModal-txt-advantageRate"
						name="advantageRate"
						label={getTranslateByKey("information.advantageFactor")}
						control={control}
						onChangeCustom={(e) => {
							const decimalPart = e.split(",");
							if (decimalPart.length > 1 && Number(decimalPart[0]) != 100) {
								return;
							}
							return setValue("advantageRate", e != "" ? `${Number(decimalPart[0])}` : e);
						}}
						onBlurCustom={(e) => {
							if (e === "") {
								return;
							}
							const decimalPart = e.split(",");
							if (
								Number(decimalPart[0]) === 0 &&
								(Number(decimalPart[1]) === 0 || decimalPart[1] === "" || decimalPart[1] === undefined)
							) {
								return setValue("advantageRate", "");
							}
							if (decimalPart[1] === "" || decimalPart[1] === undefined || Number(decimalPart[1]) === 0) {
								return setValue("advantageRate", `${Number(parseFloat(e.replace(",", ".")))}`);
							}
						}}
						inputProps={{ maxLength: MAX_LENGTH_ADVANTAGE_FACTOR }}
					/>
				</S.Container>
				<S.AreaButton>
					<SecondaryButton
						onClick={() => {
							setOpen(false);
							reset(INITIAL_FORM_STATE_SAVE);
						}}
					>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<SecondaryButton id="id-prioritySuppliersModal-button-save" type="submit" isLoading={loadingSave}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
				</S.AreaButton>
			</form>
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default PrioritySuppliersModal;
